import { List } from "immutable"
import { createSelector } from "reselect"

import { QuorumTheme } from "app/static/frontend/design-constants"

import {
    selectSpecificCustomDescription,
    makeSelectFieldFromCustomDescription,
} from "shared/state_management/selectors"
import {
    createProfileInstanceSelector,
    makeImmutableSliceSelector,
    createDataSections,
    createSectionObject,
    createCustomFieldObjects,
    createToJsSelector,
    createSocialUserObjects,
    createContactCardDetailItems,
    parseDate,
    createExtraFieldSelector,
    createTransactionSummaryRowData,
    createTransactionSummaryColumnMapping,
} from "shared/imports/sharedHelperFunctions"
import { selectLedgerSettingsListIds } from "shared/pac/selectors"
import * as socialUserSelectors from "shared/social/social-user-selectors"
import * as customFieldSelectors from "shared/customFields/selectors"
import { selectTeamNamesFromUserdataSlice } from "shared/userdata/userdata-selectors"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import {
    checkPermission,
    checkHasPermission,
    checkHasUserPermission,
    isNotAbleToViewSensitiveFields,
    hasAnyPermission,
} from "shared/imports/permissionFunctions"
import { CONTACT_SECTION_TITLES } from "shared/profiles/supporter/constants"

export const supporterSelector = (state) => state.supporterProfile

export const selectProfileInstance = createProfileInstanceSelector({
    selector: supporterSelector,
    key: "supporters",
})

const makeSupporterProfileSelect = makeImmutableSliceSelector(selectProfileInstance)

export const selectSupporter = makeSupporterProfileSelect("supporter")
export const selectSupporterJs = createToJsSelector(selectSupporter)
export const selectIsLoadingSupporter = makeSupporterProfileSelect("isLoadingSupporter")

export const selectSupporterProp = makeImmutableSliceSelector(selectSupporter)

export const selectId = selectSupporterProp("id")
export const selectName = selectSupporterProp("name", "Unnamed Contact")
export const selectTitle = selectSupporterProp("title")
export const selectEmail = selectSupporterProp("email")
export const selectSecondaryEmail = selectSupporterProp("secondary_email")
export const selectCreated = selectSupporterProp("created")
export const selectPreferredName = selectSupporterProp("preferred_name")
export const selectAddress = selectSupporterProp("address")
export const selectInputAddress = selectSupporterProp("input_address")
export const selectAlternateAddress = selectSupporterProp("alternate_address")
export const selectRegion = selectSupporterProp("region")

export const selectPublicOrganizationObj = selectSupporterProp("public_organization")
export const makePublicOrgSelect = makeImmutableSliceSelector(selectPublicOrganizationObj)
export const selectPublicOrganizationName = makePublicOrgSelect("name")
export const selectPublicOrganizationId = makePublicOrgSelect("id")

export const selectPublicOrganizations = selectSupporterProp("public_organizations")

export const selectIsGrassrootsSupporter = selectSupporterProp("is_grassroots_supporter")
export const selectIsStakeHolderSupporter = selectSupporterProp("is_stakeholder_supporter")
export const selectIsCollaboratorSupporter = selectSupporterProp("is_collaborator_supporter")
export const selectUnsubscribedFromBulkEmails = selectSupporterProp("unsubscribed_from_bulk_emails")
export const selectDateUnsubscribedFromBulkEmails = createSelector(
    selectSupporterProp("date_unsubscribed_from_bulk_emails"),
    (unsubscribed) => unsubscribed && parseDate(unsubscribed),
)
export const selectDateUnsubscribedFromTexting = createSelector(
    selectSupporterProp("date_unsubscribed_from_texting"),
    (unsubscribed) => unsubscribed && parseDate(unsubscribed),
)

const preventNoneOrEmptyValue = (func, def = "Not Set") => {
    return func?.replaceAll(/\s/g, "") != "" ? func : def
}
export const selectPaccIndividualId = selectSupporterProp("pacc_individual_id")

export const selectShouldDislayInExternalLogger = selectSupporterProp("should_display_in_external_logger")
export const selectUpdated = selectSupporterProp("updated")
export const selectValidEmail = selectSupporterProp("valid_email")
export const selectImgUrl = selectSupporterProp("image_url")
export const selectLatitude = selectSupporterProp("latitude")
export const selectLongitude = selectSupporterProp("longitude")
export const selectPhone = selectSupporterProp("phone_number")
export const selectCell = selectSupporterProp("secondary_phone")
export const selectPhoneExtension = selectSupporterProp("extension")
export const selectCellExtension = selectSupporterProp("secondary_extension")
export const selectSourceType = selectSupporterProp("source_type")
export const selectRegisteredWithTextKeyword = selectSupporterProp("registered_with_text_keyword")
export const selectEmployer = selectSupporterProp("employer")
export const selectOccupation = selectSupporterProp("occupation")
export const selectFax = selectSupporterProp("fax")
export const selectWorkFax = selectSupporterProp("work_fax")
export const selectWorkPhone = selectSupporterProp("work_phone")
export const selectActionCenterSource = createExtraFieldSelector({
    key: "action_center_source",
    objectSelector: selectSupporterProp,
})
export const selectActionCenterInteractions = createExtraFieldSelector({
    key: "action_center_interactions",
    objectSelector: selectSupporterProp,
})
export const selectRegistrationPageSource = createExtraFieldSelector({
    key: "registration_page_source",
    objectSelector: selectSupporterProp,
})
export const selectRegistrationPageInteractions = createExtraFieldSelector({
    key: "registration_page_interactions",
    objectSelector: selectSupporterProp,
})

export const selectBirthdate = createSelector(
    selectSupporterProp("birthdate"),
    (birthdate) => birthdate && parseDate(birthdate),
)

// pac classic individual data
export const selectPac2IndividualsInformation = createExtraFieldSelector({
    key: "individuals_information",
    objectSelector: selectSupporterProp,
})

export const selectPac2PacAccountInformation = createExtraFieldSelector({
    key: "pac_account_information",
    objectSelector: selectSupporterProp,
})

export const selectPac2WorkAddress = createSelector([selectPac2IndividualsInformation], (individualsInformation) => {
    if (individualsInformation) {
        const addressParts = [
            individualsInformation.get("work_address_line_1"),
            individualsInformation.get("work_address_line_2"),
            individualsInformation.get("work_city"),
            individualsInformation.get("work_state"),
            individualsInformation.get("work_zip"),
        ].filter(Boolean)

        return addressParts.length > 0 ? addressParts.join(" ") : ""
    }
})

export const selectPac2HomeAddress = createSelector([selectPac2IndividualsInformation], (individualsInformation) => {
    if (individualsInformation) {
        const addressParts = [
            individualsInformation.get("home_address_line_1"),
            individualsInformation.get("home_address_line_2"),
            individualsInformation.get("home_city"),
            individualsInformation.get("home_state"),
            individualsInformation.get("home_zip"),
        ].filter(Boolean)

        return addressParts.length > 0 ? addressParts.join(" ") : ""
    }
})

export const selectPac2HomePhone = createSelector([selectPac2IndividualsInformation], (individualsInformation) => {
    if (individualsInformation) {
        return individualsInformation.get("home_phone") || ""
    }
})

export const selectPac2PreferredEmail = createSelector([selectPac2IndividualsInformation], (individualsInformation) => {
    if (individualsInformation) {
        return individualsInformation.get("pac_email") || ""
    }
})

export const selectPac2PacClassicLinkedList = createSelector(
    [selectPac2IndividualsInformation],
    (individualsInformation) => {
        return (individualsInformation && individualsInformation.get("pac_links")) || []
    },
)

export const selectPac2PacClassicLinkedAccounts = createSelector(
    [selectPac2IndividualsInformation],
    (individualsInformation) => {
        if (individualsInformation) {
            let pac_links = individualsInformation.get("pac_links") || []
            return pac_links.map((item) => {
                return `${item.get("acronym")} `
            })
        }
        return []
    },
)

export const selectPacActivityTransactionData = createSelector(
    [selectPac2PacAccountInformation],
    (pacAccountInformation) => {
        if (pacAccountInformation) {
            return (pacAccountInformation && pacAccountInformation.get("transaction_data")) || []
        }
    },
)

export const selectLastUser = createSelector(selectSupporterProp("last_user"), (userObj) => {
    const name = userObj && userObj.get("name")
    return userObj && `${name}` + `${name ? " ( " : ""}${userObj.get("email")}${name ? " )" : ""}`
})
export const selectSupporterType = selectSupporterProp("supporter_type")
// custom data is stored on the supporter model
export const selectCustomTagsNotJS = selectSupporterProp("tag_dict")
export const selectCustomTags = createToJsSelector(selectCustomTagsNotJS)

export const selectImmutableTransactionSummary = makeSupporterProfileSelect("transactionSummary")
export const selectTransactionSummary = createToJsSelector(selectImmutableTransactionSummary)
export const selectIsLoadingTransactionSummary = makeSupporterProfileSelect("isLoadingTransactionSummary")
export const selectFetchSupporterTransactionSummaryFailed = makeSupporterProfileSelect(
    "fetchSupporterTransactionSummaryFailed",
)

// Determine whether to make a request to fetch transaction summary information
export const selectShouldLoadTransactionSummary = createSelector(
    selectIsLoadingTransactionSummary,
    selectFetchSupporterTransactionSummaryFailed,
    selectTransactionSummary,
    selectLedgerSettingsListIds,
    selectId,
    (isLoading, fetchFailed, summary, ledgerSettingsListIds, id) =>
        !isLoading &&
        !fetchFailed &&
        !summary &&
        ledgerSettingsListIds &&
        Boolean(ledgerSettingsListIds[0]) &&
        Boolean(id),
)

export const selectTransactionSummaryRowData = createSelector(selectTransactionSummary, (transactionSummary) => {
    const rowData = createTransactionSummaryRowData(transactionSummary, "year")
    // Reverse rowData for the supporter table to show recent years first [2020, 2019 ...]
    return rowData && rowData.reverse()
})

export const selectTransactionSummaryColumnMapping = createSelector(selectTransactionSummary, (transactionSummary) =>
    createTransactionSummaryColumnMapping({
        transactionSummary,
        datumLabel: "year",
        qdt: DjangIO.app.models.QuorumDataType.supporter.value,
    }),
)

export const selectBusinessCards = makeSupporterProfileSelect("businessCards", [])
export const selectIsLoadingBusinessCards = makeSupporterProfileSelect("isLoadingBusinessCards")

export const selectIsLoading = createSelector(
    selectIsLoadingSupporter,
    selectIsLoadingBusinessCards,
    customFieldSelectors.selectIsLoading,
    (loadingSup, loadingCards, loadingCustomFields) => loadingSup || loadingCards || loadingCustomFields,
)

export const selectSupporterCustomDescription = selectSpecificCustomDescription(
    DjangIO.app.models.QuorumDataType.supporter.key,
)

const makeSupporterDescriptionSelect = makeSelectFieldFromCustomDescription(selectSupporterCustomDescription)

/** @type {(state: any, props?: any) => any} */
export const selectCustomDescriptionText = makeSupporterDescriptionSelect("text")
export const selectCustomDescriptionId = makeSupporterDescriptionSelect("id")

// // profiles-new
export const selectCurrentTab = makeSupporterDescriptionSelect("currentTab")

export const selectUniqueIdentifier = selectSupporterProp("unique_identifier")

export const selectLimitedToTeams = selectSupporterProp("limited_to_teams")
export const selectTeamsForProfile = selectTeamNamesFromUserdataSlice(selectLimitedToTeams)

export const selectGamificationStats = createToJsSelector(
    createExtraFieldSelector({
        key: "gamification_stats",
        objectSelector: selectSupporterProp,
    }),
    {},
)

export const selectCharityName = selectSupporterProp("pac_charity_name")
export const selectCharityEIN = selectSupporterProp("pac_charity_ein")
export const selectCharityAddress = selectSupporterProp("pac_charity_address")
export const selectPacCharityAnonymous = selectSupporterProp("pac_charity_anonymous")
export const selectPacCharityAdditionalInformation = selectSupporterProp("pac_charity_additional_info")

// PAC 2 Fields

export const selectPacAccountInformation = createToJsSelector(
    createExtraFieldSelector({
        key: "pac_account_information",
        objectSelector: selectSupporterProp,
    }),
    {},
)

export const selectSupporterPacActivityTable = ({ sectionComponent }) =>
    createSelector([selectPacAccountInformation], (pacAccountInformation) => {
        return checkHasUserPermission([
            "can_access_pac",
            "can_view_pac_disbursements",
            "can_view_pac_receipts",
            "can_alter_pac_transactions",
        ])
            ? createSectionObject({
                  title: "Transactions Table",
                  id: "supporterPacActivityTable",
                  data: [
                      {
                          pac_activity_data: pacAccountInformation,
                      },
                  ],
                  sectionComponent,
              })
            : null
    })

export const selectGamificationSectionData = createSelector(selectGamificationStats, (stats) =>
    Object.entries(stats).reduce((sectionData, [actionCenterName, statistics]) => {
        sectionData.push({
            title: `${actionCenterName} Points`,
            // Accounts for 0 or undefined or null points
            value: statistics.points ? String(statistics.points) : "0",
        })
        sectionData.push({
            title: `${actionCenterName} Level`,
            // If have not reached a level, default to 'N/A' for blank styling in profiles-new/components/DataTableView/DataItem.jsx
            value: statistics.level || "N/A",
        })
        return sectionData
    }, []),
)

const selectPacEligibilities = selectSupporterProp("pac_eligibilities")
const selectPacHybridEligible = selectSupporterProp("pac_hybrid_eligible")
const selectPacConditionalGivingLevel = selectSupporterProp("pac_conditional_giving_level")
const selectPacCharitySelectionDate = selectSupporterProp("pac_charity_selection_date")

export const selectIsPacHybridEligible = createSelector(selectPacHybridEligible, (pacHybridEligible) =>
    pacHybridEligible ? "Yes" : "No",
)

export const selectIsPacEligible = createSelector(selectPacEligibilities, (eligibilitiesList) =>
    eligibilitiesList && eligibilitiesList.size > 0 ? "Yes" : "No",
)

export const selectUserIsAbleToSeeLinkToPACButton = createSelector(selectPac2PacAccountInformation, (paccAccount) => {
    // Check if user has PAC 2.0 organization permission AND PAC management access
    const hasOrgPermission = checkPermission(DjangIO.app.models.PermissionType.pac_hybrid)
    // Remove the permission later
    const hasUserPermission =
        checkHasUserPermission(["can_access_pac_management"]) || checkHasUserPermission(["can_access_pac"])

    return hasOrgPermission && hasUserPermission && paccAccount === undefined
})

export const selectUserShouldSeeManagePacDetails = createSelector(selectPac2PacAccountInformation, (paccAccount) => {
    // Check if user has PAC 2.0 organization permission AND PAC management access
    const hasOrgPermission = checkPermission(DjangIO.app.models.PermissionType.pac_hybrid)
    // Remove the permission later
    const hasUserPermission =
        checkHasUserPermission(["can_access_pac_management"]) || checkHasUserPermission(["can_access_pac"])

    return hasOrgPermission && hasUserPermission && paccAccount !== undefined
})

export const selectSupporterIsPACContact = createSelector(
    selectPaccIndividualId,
    selectPac2PacClassicLinkedAccounts,
    (paccIndividualId, pac2PacClassicLinkedAccounts) => {
        // Check if contact is linked to PAC AND user has PAC management permission
        const isLinkedToPAC = paccIndividualId !== null && pac2PacClassicLinkedAccounts.size > 0
        const hasOrgPermission = checkPermission(DjangIO.app.models.PermissionType.pac_hybrid)
        // Remove the permission later
        const hasUserPermission =
            checkHasUserPermission(["can_access_pac_management"]) || checkHasUserPermission(["can_access_pac"])

        return isLinkedToPAC && hasOrgPermission && hasUserPermission
    },
)

/**
 * Truthy values become "Yes". Null and undefined become null. All other values become "No".
 */
const yesNoNull = (val) => {
    if (val) {
        return "Yes"
    } else if (val === null || val === undefined) {
        return null
    } else {
        return "No"
    }
}

// Selector to determine which PAC eligibility value to use based on organization's PAC version
export const selectPacEligibilityValue = createSelector(
    [selectIsPacEligible, selectIsPacHybridEligible],
    (isPacEligible, isPacHybridEligible) => {
        if (checkHasPermission([DjangIO.app.models.PermissionType.pac_hybrid])) {
            return isPacHybridEligible
        } else if (checkHasPermission([DjangIO.app.models.PermissionType.qpac_v1])) {
            return isPacEligible
        }
        // Fallback to legacy PAC eligibility if no specific permissions are found
        return isPacEligible
    },
)

export const selectSupporterPacDetailsSections = ({ sectionComponent }) =>
    createSelector(
        [
            selectPac2WorkAddress,
            selectPac2HomeAddress,
            selectPac2PacClassicLinkedAccounts,
            selectPacEligibilityValue,
            selectPacConditionalGivingLevel,
            selectEmployer,
            selectOccupation,
        ],
        (
            pac2WorkAddress,
            pac2HomeAddress,
            pac2PacClassicLinkedAccounts,
            pacEligibilityValue,
            pacConditionalGivingLevel,
            employer,
            occupation,
        ) => {
            return checkHasUserPermission(["can_access_pac"])
                ? createSectionObject({
                      title: "PAC Details",
                      id: "supporterPacDetailsSections",
                      data: [
                          {
                              title: "PAC Eligible",
                              subtitle: preventNoneOrEmptyValue(pacEligibilityValue),
                          },
                          {
                              title: "Conditional Giving Level",
                              subtitle: preventNoneOrEmptyValue(pacConditionalGivingLevel, "No Level Assigned"),
                          },
                          {
                              title: "Employer",
                              subtitle: preventNoneOrEmptyValue(employer),
                          },
                          {
                              title: "Occupation",
                              subtitle: preventNoneOrEmptyValue(occupation),
                          },
                          {
                              title: "PAC Links",
                              subtitle: pac2PacClassicLinkedAccounts?.size ? pac2PacClassicLinkedAccounts : "Not Set",
                          },
                          {
                              title: "Work Address",
                              subtitle: preventNoneOrEmptyValue(pac2WorkAddress, " "),
                          },
                          {
                              title: "Home Address",
                              subtitle: preventNoneOrEmptyValue(pac2HomeAddress, " "),
                          },
                      ],
                      sectionComponent,
                  })
                : null
        },
    )

export const selectSupporterInfoSections = ({ isMobile, selectorFunctions, sectionComponent, dataItems }) =>
    createSelector(
        [
            selectPublicOrganizationName,
            selectPublicOrganizationId,
            selectTitle,
            selectEmail,
            selectSecondaryEmail,
            selectAddress,
            selectIsGrassrootsSupporter,
            selectIsStakeHolderSupporter,
            selectIsCollaboratorSupporter,
            selectValidEmail,
            selectId,
            socialUserSelectors.selectSocialUsersFromModel(
                DjangIO.app.grassroots.models.Supporter.__name__,
                selectSupporter,
            ),
            selectPhone,
            selectCell,
            selectBusinessCards,
            selectInputAddress,
            selectPreferredName,
            selectAlternateAddress,
            selectPhoneExtension,
            selectCellExtension,
            selectBirthdate,
            selectUniqueIdentifier,
            selectTeamsForProfile,
            selectShouldDislayInExternalLogger,
            selectPacEligibilityValue,
            selectPacConditionalGivingLevel,
            selectEmployer,
            selectOccupation,
            selectFax,
            selectWorkPhone,
            selectWorkFax,
            selectPacCharitySelectionDate,
            selectCharityName,
            selectCharityEIN,
            selectCharityAddress,
            selectPacCharityAnonymous,
            selectPacCharityAdditionalInformation,
        ],
        (
            organizationName,
            organizationId,
            title,
            email,
            secondaryEmail,
            address,
            isGrassrootsSupporter,
            isStakeHolderSupporter,
            isCollaboratorSupporter,
            hasValidEmail,
            datumId,
            socialUsers,
            phone,
            cell,
            businessCards,
            inputAddress,
            preferredName,
            alternateAddress,
            extension,
            secondaryExtension,
            birthdate,
            uniqueIdentifier,
            teamsForProfile,
            shouldDisplayInExternalLogger,
            pacEligibilityValue,
            pacConditionalGivingLevel,
            employer,
            occupation,
            fax,
            workPhone,
            workFax,
            charitySelectionDate,
            charityName,
            charityEIN,
            charityAddress,
            charityAnonymous,
            charityAdditionalInformation,
        ) => {
            const addressToUse = inputAddress || address
            const sectionData = [
                {
                    title: "Organization",
                    value: organizationName,
                    onPress: selectorFunctions && selectorFunctions({ organizationName, organizationId }).organization,
                    icon: organizationName && organizationId ? "angle-right" : "",
                    isMobile: true,
                },
                {
                    title: "Email",
                    value: email,
                    onPress: selectorFunctions && selectorFunctions({ email }).email,
                    icon: "envelope-o",
                },
                {
                    title: "Secondary Email",
                    value: secondaryEmail,
                    onPress: selectorFunctions && selectorFunctions({ secondaryEmail }).secondaryEmail,
                    icon: "envelope-o",
                },
                {
                    title: "Title",
                    value: title,
                },
                {
                    title: "Office Phone",
                    value: [phone, extension].filter((i) => i).join(" | Ex. "),
                    onPress: selectorFunctions && selectorFunctions({ phone }).phone,
                    icon: "phone",
                },
                ...(!isNotAbleToViewSensitiveFields
                    ? [
                          {
                              title: "Cell Phone",
                              value: [cell, secondaryExtension].filter((i) => i).join(" | Ex. "),
                              onPress: selectorFunctions && selectorFunctions({ cell }).cell,
                              icon: "phone",
                          },
                      ]
                    : []),
                {
                    title: "Input Address",
                    value: addressToUse,
                    onPress: selectorFunctions && selectorFunctions({ address: addressToUse }).address,
                    icon: "map-marker",
                    width: 6,
                },
                {
                    title: "Alternate Address",
                    value: alternateAddress,
                    onPress: selectorFunctions && selectorFunctions({ alternateAddress }).alternateAddress,
                    icon: "map-marker",
                    width: 6,
                },
                {
                    title: "Preferred Name",
                    value: preferredName,
                    onPress: selectorFunctions && selectorFunctions({ preferredName }).preferredName,
                },
                {
                    title: "Unique Identifier",
                    value: uniqueIdentifier,
                },
                {
                    title: "Grassroots Supporter",
                    value: isGrassrootsSupporter ? "Yes" : "No",
                },
                {
                    title: "Stakeholder Supporter",
                    value: isStakeHolderSupporter ? "Yes" : "No",
                },
                ...(isFeatureEnabled("ff_contributor_license_addon") &&
                hasAnyPermission([DjangIO.app.models.PermissionType.collaborators])
                    ? [
                          {
                              title: "Collaborator Contact",
                              value: isCollaboratorSupporter ? "Yes" : "No",
                          },
                      ]
                    : []),
                {
                    title: "Birthday",
                    value: birthdate,
                },
                {
                    title: "Limited to Teams",
                    value: teamsForProfile,
                },
                ...(businessCards
                    ? businessCards.reduce(
                          (cards, card) =>
                              cards.concat([
                                  {
                                      title: "Business Card (Front)",
                                      value: card.get("front_img_url"),
                                      onPress:
                                          selectorFunctions &&
                                          selectorFunctions({ frontUrl: card.get("front_img_url") }).cardFront,
                                      icon: "angle-right",
                                      hideValue: true,
                                      isMobile: true,
                                  },
                                  {
                                      title: "Business Card (Back)",
                                      value: card.get("back_img_url"),
                                      onPress:
                                          selectorFunctions &&
                                          selectorFunctions({ backUrl: card.get("back_img_url") }).cardBack,
                                      icon: "angle-right",
                                      hideValue: true,
                                      isMobile: true,
                                  },
                              ]),
                          [],
                      )
                    : []),
                {
                    title: "Valid Email",
                    value: hasValidEmail ? "Yes" : "No",
                },
                ...createSocialUserObjects({
                    socialUsers: socialUsers.toJS ? socialUsers.toJS() : socialUsers,
                    selectorFunctions,
                    dataItems,
                }),
                {
                    title: "Display in External Interaction/Relationship Logger",
                    value: shouldDisplayInExternalLogger ? "Yes" : "No",
                    permissions: [
                        DjangIO.app.models.PermissionType.external_interactions,
                        DjangIO.app.models.PermissionType.external_relationship,
                    ],
                },
                ...(checkHasPermission([
                    DjangIO.app.models.PermissionType.qpac_v1,
                    DjangIO.app.models.PermissionType.pac_hybrid,
                ])
                    ? [
                          {
                              title: "Conditional Giving Level",
                              value: pacConditionalGivingLevel,
                          },
                      ]
                    : []),
                ...(isFeatureEnabled("ff_pac")
                    ? [
                          {
                              title: "Employer",
                              value: employer,
                          },
                      ]
                    : []),
                ...(isFeatureEnabled("ff_pac")
                    ? [
                          {
                              title: "Occupation",
                              value: occupation,
                          },
                      ]
                    : []),
                ...(isFeatureEnabled("ff_pac")
                    ? [
                          {
                              title: "Fax",
                              value: fax,
                          },
                      ]
                    : []),
                ...(isFeatureEnabled("ff_pac")
                    ? [
                          {
                              title: "Work Phone",
                              value: workPhone,
                          },
                      ]
                    : []),
                ...(isFeatureEnabled("ff_pac")
                    ? [
                          {
                              title: "Work Fax",
                              value: workFax,
                          },
                      ]
                    : []),
                ...(checkPermission(DjangIO.app.models.PermissionType.pac_match) &&
                charityName &&
                isFeatureEnabled("ff_pac_charitable_match_search")
                    ? [
                          { title: "Charity", value: charityName },
                          { title: "Charity EIN", value: charityEIN },
                          { title: "Charity Address", value: charityAddress },
                          {
                              title: "Charity Selection Date",
                              value: charitySelectionDate
                                  ? new Date(charitySelectionDate).toLocaleDateString()
                                  : "None",
                          },
                          {
                              title: "Charity Remain Anonymous?",
                              value: yesNoNull(charityAnonymous),
                          },
                          {
                              title: "Charity Additional Information",
                              value: charityAdditionalInformation,
                          },
                      ]
                    : []),
            ]
            const infoDataSections = createDataSections(
                !isMobile ? sectionData.filter((dataObj) => !dataObj.isMobile) : sectionData,
            )

            const sectionObj = createSectionObject({
                title: CONTACT_SECTION_TITLES.information,
                data: infoDataSections,
                id: "supporterInfoSections",
                sectionComponent,
            })
            return sectionObj
        },
    )

export const selectCustomDataFields = ({ sectionComponent }) =>
    createSelector(
        selectCustomTags,
        customFieldSelectors.selectCustomFieldNameDictForProfile(DjangIO.app.models.QuorumDataType.supporter.value),
        customFieldSelectors.selectCustomFieldNameDictForProfile(
            DjangIO.app.models.QuorumDataType.grassroots_action.value,
        ),
        (tagDict, supporterCustomFields, actionCustomFields) => {
            const customFields = { ...supporterCustomFields, ...actionCustomFields }
            const customData = [
                ...createCustomFieldObjects({ customFields, tagDict }).map((dataObj) => Object.assign(dataObj)),
            ]
            const customDataObj = createSectionObject({
                title: CONTACT_SECTION_TITLES.customFields,
                data: createDataSections(customData),
                id: "supporterCustomData",
                sectionComponent,
                canEditSection: true,
            })
            return customDataObj
        },
    )

/**
 * Select the enum label associated with the calculated unsubscribed from
 * texting API field; this is different from the unsubscribed_from_texting
 * field on the supporter model because it excludes supporters who have never
 * sent an inbound text
 **/
export const selectCalculatedUnsubscribedFromTextingText = createSelector(
    selectSupporterProp("calculated_unsubscribed_from_texting"),
    (unsubscribedEnum) =>
        unsubscribedEnum && DjangIO.app.texting.types.SubscriptionStatus.by_value(unsubscribedEnum).label,
)

/**
 * Select the enum label associated with the unsubscribed_from_texting field
 * state directly, without using the calculated_unsubscribed_from_texting
 *
 * With the introduction of outbound texting,
 * the calculated_unsubscribed_from_texting is out of date. for_texting(),
 * the querymethod that determines outbound texting eligibility, uses different
 * criteria than calculated_unsubscribed_from_texting
 **/
export const selectUnsubscribedFromTextingText = createSelector(
    selectSupporterProp("unsubscribed_from_texting"),
    (unsubscribedValue) => {
        if (unsubscribedValue === true) {
            return DjangIO.app.texting.types.SubscriptionStatus.unsubscribed.label
        } else if (unsubscribedValue === false) {
            return DjangIO.app.texting.types.SubscriptionStatus.subscribed.label
        } else if (unsubscribedValue === null) {
            return DjangIO.app.texting.types.SubscriptionStatus.not_yet_subscribed.label
        } else {
            return undefined
        }
    },
)

export const selectQuorumGenerated = ({ sectionComponent }) =>
    createSelector(
        [
            selectSourceType,
            selectCreated,
            selectUpdated,
            selectUnsubscribedFromBulkEmails,
            selectDateUnsubscribedFromBulkEmails,
            selectUnsubscribedFromTextingText,
            selectDateUnsubscribedFromTexting,
            selectLastUser,
            selectRegisteredWithTextKeyword,
            selectActionCenterSource,
            selectActionCenterInteractions,
            selectRegistrationPageSource,
            selectRegistrationPageInteractions,
            selectGamificationSectionData,
        ],
        (
            contactSource,
            created,
            updated,
            unsubscribedFromBulkEmails,
            dateUnsubscribedFromBulkEmails,
            unsubscribedFromTextingText,
            dateUnsubscribedFromTexting,
            lastUser,
            registeredWithTextKeyword,
            actionCenterSource,
            actionCenterInteractions,
            registrationPageSource,
            registrationPageInteractions,
            gamificationSectionData,
        ) => {
            const sectionData = [
                {
                    title: "Date Created",
                    value: created,
                },
                {
                    title: "Last Updated",
                    value: updated,
                },
                {
                    title: "Last Modified By",
                    value: lastUser,
                },
                {
                    title: "Contact Source",
                    value:
                        DjangIO.app.grassroots.types.UTMSourceType.by_value(contactSource) &&
                        DjangIO.app.grassroots.types.UTMSourceType.by_value(contactSource).label,
                },
                {
                    title: "Unsubscribed from Outbox",
                    value: unsubscribedFromBulkEmails ? "Yes" : "No",
                },
                {
                    title: "Date Unsubscribed from Outbox",
                    value: dateUnsubscribedFromBulkEmails,
                },
                ...(checkPermission(DjangIO.app.models.PermissionType.outbound_texting)
                    ? [
                          {
                              title: "Unsubscribed from Text Messages",
                              value: unsubscribedFromTextingText,
                          },
                          {
                              title: "Date Unsubscribed from Text Messages",
                              value: dateUnsubscribedFromTexting,
                          },
                          {
                              title: "Registered With Text Keyword",
                              value: registeredWithTextKeyword,
                          },
                      ]
                    : []),
                {
                    title: "Action Center Source",
                    value: actionCenterSource,
                },
                {
                    title: "Action Centers Visited",
                    value: actionCenterInteractions,
                },
                {
                    title: "Registration Form Source",
                    value: registrationPageSource,
                },
                {
                    title: "Registration Forms Used",
                    value: registrationPageInteractions,
                },
                ...gamificationSectionData,
            ]

            const sectionObj = createSectionObject({
                title: CONTACT_SECTION_TITLES.quorumGenerated,
                data: createDataSections(sectionData),
                id: "quorumGeneratedInfoSections",
                sectionComponent,
            })

            return sectionObj
        },
    )

export const selectPublicOrganizationInfo = createSelector(
    selectPublicOrganizationName,
    selectPublicOrganizationId,
    selectPublicOrganizations,
    (publicOrganizationName, publicOrganizationId, publicOrganizations) => {
        let publicOrganizationInfo = []

        if (List.isList(publicOrganizations)) {
            publicOrganizationInfo = publicOrganizations.reduce((orgs, organization) => {
                if (organization && organization.get) {
                    orgs.push({
                        href: `${DjangIO.app.models.QuorumDataType.public_organization.profile}${organization.get(
                            "id",
                        )}`,
                        label: "Org",
                        text: organization.get("name"),
                        textType: "primary",
                    })
                }
                return orgs
            }, [])
        }

        return publicOrganizationInfo
    },
)

export const selectContactCardData = ({ selectorFunctions, sectionComponent }) =>
    createSelector(
        selectName,
        selectTitle,
        selectPublicOrganizationInfo,
        selectEmail,
        selectPhone,
        selectAddress,
        selectImgUrl,
        selectSecondaryEmail,
        selectAlternateAddress,
        selectCell,
        selectPac2WorkAddress,
        selectPac2HomeAddress,
        selectPac2PreferredEmail,
        selectPac2HomePhone,
        (
            name,
            title,
            publicOrganizationInfo,
            email,
            phoneNumber,
            address,
            imageUrl,
            secondaryEmail,
            alternateAddress,
            cellPhone,
            pacWorkAddress,
            pacHomeAddress,
            pacEmail,
            pacPhoneNumber,
        ) => {
            const contactData = [
                {
                    circleColor: QuorumTheme.colors.gray[4],
                    detailItems: createContactCardDetailItems({ email, phoneNumber, address }),
                    infoItems: [
                        title && {
                            label: "Title",
                            text: title,
                            textType: "secondary",
                        },
                        ...publicOrganizationInfo,
                    ],
                    secondaryItems: [
                        {
                            label: "Secondary Email",
                            text: secondaryEmail,
                        },
                        {
                            label: "Alternate Address",
                            text: alternateAddress,
                        },
                        {
                            label: "Cell Phone",
                            text: cellPhone,
                        },
                    ],
                    pacItems: [
                        {
                            label: "Email",
                            text: pacEmail,
                        },
                        {
                            label: "Phone Number",
                            text: pacPhoneNumber,
                        },
                        {
                            label: "Work Address",
                            text: pacWorkAddress,
                        },
                        {
                            label: "Home Address",
                            text: pacHomeAddress,
                        },
                    ],
                    icon: DjangIO.app.models.QuorumDataType.supporter.fa5_icon,
                    iconFamily: DjangIO.app.models.QuorumDataType.supporter.fa5_icon_family,
                    imageUrl,
                    isV2Image: true,
                    name,
                    isContactDataType: true,
                },
            ]
            const contactCardObj = createSectionObject({
                title: CONTACT_SECTION_TITLES.contactCard,
                data: contactData,
                id: "supporterContactCard",
                sectionComponent,
            })

            return contactCardObj
        },
    )

export const selectTableSection = ({ sectionComponent }) =>
    createSelector(
        selectId,
        selectTransactionSummaryRowData,
        selectTransactionSummaryColumnMapping,
        (id, rowData, columnMapping) =>
            createSectionObject({
                title: "Table",
                data: [{ id, rowData, columnMapping }],
                sectionComponent,
            }),
    )

const selectImmutablePotentialDuplicates = makeSupporterProfileSelect("potentialDuplicates")
const selectPotentialDuplicates = createToJsSelector(selectImmutablePotentialDuplicates)

export const selectPotentialDuplicatesSection = ({ sectionComponent }) =>
    createSelector(selectId, selectPotentialDuplicates, (id, potentialDuplicates) => {
        return createSectionObject({
            title: "Duplicate Management",
            id,
            data: potentialDuplicates ? potentialDuplicates : [],
            sectionComponent,
        })
    })

export const selectHeaderTitlePrefix = () => DjangIO.app.models.QuorumDataType.supporter.singular
